import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { LogOut, Menu, User as UserIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { getInitials } from '@/lib/utils';
import { NotificationBell } from './NotificationBell';
import { ThemePicker } from './ThemePicker';
import { useAuthStore } from '@/stores/auth-store';

interface TopbarProps {
  onOpenSidebar: () => void;
}

/** Nama peran yang enak dibaca, menghindari SCREAMING_SNAKE_CASE di UI. */
const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  PROVINCE_ADMIN: 'Admin Provinsi',
  REGENCY_ADMIN: 'Admin Kabupaten',
  YAYASAN: 'Yayasan',
  KEPALA_SEKOLAH: 'Kepala Sekolah',
  OPERATOR: 'Operator',
  GURU: 'Guru',
  BENDAHARA: 'Bendahara',
  ORANG_TUA: 'Orang Tua',
};

export function Topbar({ onOpenSidebar }: TopbarProps) {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);

  const handleLogout = async () => {
    await logout();
    toast.success('Anda telah keluar');
    navigate('/masuk', { replace: true });
  };

  const primaryRole = user?.roles[0];

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-[var(--color-border)] bg-[var(--color-background)]/80 px-4 backdrop-blur-sm lg:px-6">
      <button
        type="button"
        onClick={onOpenSidebar}
        className="rounded-[var(--radius-sm)] p-2 text-[var(--color-muted-foreground)] hover:bg-[var(--color-accent)] lg:hidden"
        aria-label="Buka menu"
      >
        <Menu className="size-5" aria-hidden="true" />
      </button>

      <div className="flex-1" />

      <NotificationBell />

      <ThemePicker />

      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className="flex items-center gap-2.5 rounded-[var(--radius-md)] p-1.5 transition-colors hover:bg-[var(--color-accent)]"
            aria-label="Menu akun"
          >
            <span
              className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[var(--color-primary)] text-xs font-semibold text-[var(--color-primary-foreground)]"
              aria-hidden="true"
            >
              {user ? getInitials(user.name) : '?'}
            </span>
            <span className="hidden text-left sm:block">
              <span className="block max-w-[10rem] truncate text-sm font-medium leading-tight">
                {user?.name}
              </span>
              <span className="block text-xs text-[var(--color-muted-foreground)]">
                {primaryRole ? (ROLE_LABELS[primaryRole] ?? primaryRole) : ''}
              </span>
            </span>
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={8}
            className="z-50 min-w-56 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-popover)] p-1 shadow-[var(--shadow-popover)]"
          >
            <div className="border-b border-[var(--color-border)] px-3 py-2.5">
              <p className="truncate text-sm font-medium">{user?.name}</p>
              {/* Akun wali murid masuk dengan NIS, bukan surel — barisnya
                  kosong kalau `email` dipakai begitu saja. */}
              <p className="truncate text-xs text-[var(--color-muted-foreground)]">
                {user?.email ?? user?.username}
              </p>
            </div>

            <DropdownMenu.Item
              onSelect={() => navigate('/profil')}
              className="flex cursor-pointer items-center gap-2 rounded-[var(--radius-sm)] px-3 py-2 text-sm outline-none data-[highlighted]:bg-[var(--color-accent)]"
            >
              <UserIcon className="size-4" aria-hidden="true" />
              Profil Saya
            </DropdownMenu.Item>

            <DropdownMenu.Separator className="my-1 h-px bg-[var(--color-border)]" />

            <DropdownMenu.Item
              onSelect={handleLogout}
              className="flex cursor-pointer items-center gap-2 rounded-[var(--radius-sm)] px-3 py-2 text-sm text-[var(--color-destructive)] outline-none data-[highlighted]:bg-[var(--color-destructive-subtle)]"
            >
              <LogOut className="size-4" aria-hidden="true" />
              Keluar
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </header>
  );
}
